import {Button, Card, CardBody, CardHeader, Option, Select, Typography,} from "@material-tailwind/react";
import React, {useEffect, useState, useCallback, useRef} from "react";
import {AgentsGrid} from "./grid";
import {AgentsDialog} from "./dialog";
import {getAgentLogs, getAgentLogsPerson} from "../../../services/agent/agent_log";
import {getConfirmationComments} from "../../../services/confirmation_comment/confirmation_comment";
import {CardFooterPaginator} from "../../system/cardFooterPaginator";
import SelectInput from "../../system/select";
import {AgentRequestContext} from "../../../context/agent_context";
import {PaginateContext} from "../../../context/paginate";
import Dict from "../../../helper/dict";
import {LoadingOverlay} from "../../system/loading_overlay";
import {Toaster} from "../../../helper/Toaster";

export function AgentsManagement() {

  const [agentLogs, setAgentLogs] = useState([]);
  const [confirmationComments, setConfirmationComments] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [gridRowCount, setGridRowCount] = useState(10);
  const [gridCurrentPage, setGridCurrentPage] = useState(1);
  const [gridTotalRowCount, setGridTotalRowCount] = useState(0);
  const [sortBy, setSortBy] = useState("new_branches")
  const [sortType, setSortType] = useState(false);
  const [from, setFrom] = useState((gridCurrentPage - 1) * gridRowCount + 1);
  const [to, setTo] = useState((gridCurrentPage) * gridRowCount);
  const [lastDataRefresh, setLastDataRefresh] = useState(null);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef(null);

  const [personId, setPersonId] = useState(null);
  const [status, setStatus] = useState("all");

  const [agentId, setAgentId] = useState(null);
  const [agentRequest, setAgentRequest] = useState({});
  const [agentLog, setAgentLog] = useState(null);
  const [agentBranchList, setAgentBranchList] = useState([]);
  const [requestBranchListNew, setRequestBranchListNew] = useState([]);
  const [agentDisplayDetails, setAgentDisplayDetails] = useState({});

  useEffect(() => {
    if ((gridCurrentPage) * gridRowCount <= gridTotalRowCount) {
      setTo((gridCurrentPage) * gridRowCount)
    } else {
      setTo(gridTotalRowCount)
    }
    if (gridTotalRowCount === 0) {
      setFrom(0)
    } else {
      setFrom((gridCurrentPage - 1) * gridRowCount + 1)
    }
  }, [gridCurrentPage, gridRowCount, gridTotalRowCount]);

  async function getData(paginate) {
    if (loading) return; // Prevent multiple simultaneous requests
    
    try {
      setLoading(true);
      const agentLogsRes = await getAgentLogs(paginate);
      const confirmationCommentsRes = await getConfirmationComments();
      if (agentLogsRes.status === 200 && confirmationCommentsRes.status === 200) {
        const {agent_Logs, total_count} = agentLogsRes.data;
        setAgentLogs(agent_Logs || []);
        setGridTotalRowCount(total_count);
        const {ConfirmationCommentList} = confirmationCommentsRes.data;
        setConfirmationComments(ConfirmationCommentList || []);
      } else {
        Toaster.failure(null, <Typography variant="small">
          {agentLogsRes.data?.message || confirmationCommentsRes.data?.message || 'خطا در دریافت اطلاعات'}
        </Typography>);
      }
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">
        {err.message || 'خطا در دریافت اطلاعات'}
      </Typography>);
      console.error('Agents data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }

  const debouncedGetData = useCallback((paginate) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      getData(paginate);
    }, 500); // 500ms debounce
  }, []);

  useEffect(() => {
    const paginateParams = new URLSearchParams({
      "sort_by": sortBy,
      "sort_type": sortType === false ? "DESC" : "ASC",
      "limit": parseInt(gridRowCount),
      "offset": (parseInt(gridCurrentPage) - 1) * parseInt(gridRowCount),
    })

    if (personId) {
      paginateParams.append("person_id", personId);
    }

    if (status !== "all") {
      paginateParams.append("status", status);
    }

    const paginate = paginateParams.toString()
    
    // For pagination and sorting, call immediately
    if (gridCurrentPage !== 1 || sortBy !== 'new_branches' || sortType !== false) {
      getData(paginate);
    } else {
      // For filters, use debouncing
      debouncedGetData(paginate);
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [personId, status, lastDataRefresh, gridCurrentPage, gridRowCount, sortBy, sortType, debouncedGetData]);

  useEffect(() => {
    async function getData() {
      const result = await getConfirmationComments();
      if (result.status === 200) {
        const {ConfirmationCommentList} = result.data;
        setConfirmationComments(ConfirmationCommentList || []);
      }
    }

    getData()
  }, []);

  return (
    <AgentRequestContext.Provider value={{
      agentId: agentId,
      setAgentId: setAgentId,
      modalOpen: modalOpen,
      setModalOpen: setModalOpen,
      lastDataRefresh: lastDataRefresh,
      setLastDataRefresh: setLastDataRefresh,
      confirmationComments: confirmationComments,
      agentLogs: agentLogs,
      setAgentLogs: setAgentLogs,
      agentRequest: agentRequest,
      setAgentRequest: setAgentRequest,
      agentDisplayDetails: agentDisplayDetails,
      setAgentDisplayDetails: setAgentDisplayDetails,
      agentBranchList: agentBranchList,
      setAgentBranchList: setAgentBranchList,
      requestBranchListNew: requestBranchListNew,
      setRequestBranchListNew: setRequestBranchListNew,
      agentLog: agentLog,
      setAgentLog: setAgentLog,
    }}>
      <PaginateContext.Provider value={{
        setSortBy: setSortBy,
        sortType: sortType,
        setSortType: setSortType,
        active: gridCurrentPage,
        setActive: setGridCurrentPage,
        totalRows: gridTotalRowCount,
        rowsCount: gridRowCount,
        setRowsCount: setGridRowCount,
        from: from,
        to: to,
      }}>
        <>
          <AgentsDialog/>
          <Card className="pt-8">
            <CardHeader floated={true} shadow={false} className="rounded-none">
              <div className="mb-8 flex items-center justify-between gap-8">
                <Typography variant="h5" color="blue-gray">مشاوران</Typography>
              </div>

            </CardHeader>
            <CardBody className="overflow-y-scroll overflow-x-auto w-full">

              <div className="grid grid-cols-1 gap-8 mb-2 md:grid-cols-2">

                <div className="flex flex-col">
                  <SelectInput
                    label={"مباشر"}
                    defaultValue={personId}
                    refreshSignal={personId}
                    // multiselect={false}
                    autoLoadService={getAgentLogsPerson}
                    autoLoadRender={({name, phone}) => {
                      return `${name} - ${phone}`
                    }}
                    onChange={setPersonId}
                  />
                </div>

                <div className="flex flex-col">
                  <Select variant="static" className="rtl" style={{label: {left: ""}}}
                          label={Dict('status')}
                          value={status}
                          onChange={(val) =>
                            setStatus(val)
                          }>
                    <Option value="all">همه</Option>
                    <Option value="accepted">قبول</Option>
                    <Option value="rejected">رد</Option>
                    <Option value="edited">ویرایش</Option>
                    <Option value="new">جدید</Option>
                  </Select>
                </div>

              </div>


              {loading && <LoadingOverlay />}

              <div className="relative">
                <AgentsGrid/>
              </div>

              <div className="flex flex-row">
                <Button variant="gradient" color="gray" onClick={() => {
                  setPersonId(null);
                }} className="ml-1" disabled={loading}>
                  <span>حذف فیلتر</span>
                </Button>
              </div>
            </CardBody>
            <CardFooterPaginator/>
          </Card>
        </>
      </PaginateContext.Provider>
    </AgentRequestContext.Provider>
  )

}